import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import Auth from '../../../api/auth';
import DeleteFormModal from '../../../components/DeleteFormModal/DeleteFormModal';
import useApiQuery from '../../../hooks/useApiQuery';

import ProductUnassignment from './ProductUnassignment';

import type { APIHookArg } from '@/types';
import type { Product } from '@/types/productTypes';
import type { User, UserResources } from '@/types/user';

interface DeleteUserFormModalProps {
  onSubmit: () => void;
  onCancel: () => void;
  user: User;
}

const DeleteUserFormModal = ({
  onSubmit,
  onCancel,
  user,
}: DeleteUserFormModalProps): React.JSX.Element => {
  const navigate = useNavigate();

  const [userResources, setUserResources] = useState<UserResources>({ products: [] });

  const { makeApiCall: getUserResourcesCall } = useApiQuery<UserResources>(
    Auth.fetchV3UserResources as unknown as (args: unknown) => Promise<UserResources>
  );
  const { makeApiCall: deleteUserCall } = useApiQuery(Auth.deleteV3User as unknown as APIHookArg, {
    successMessage: 'userDeleted',
  });

  useEffect(() => {
    const getUserResources = async (): Promise<void> => {
      if (!userResources.products?.length) {
        const data = await getUserResourcesCall(user.uuid);
        if (data) setUserResources(data);
      }
    };
    getUserResources();
  }, [getUserResourcesCall, user.uuid, userResources.products?.length]);

  const deleteUser = async (): Promise<void> => {
    onSubmit();
    const data = await deleteUserCall(user.uuid);

    if (data) {
      navigate(`/governance/organizations/${user.organizationId}/users`);
    }
  };

  return (
    <DeleteFormModal
      fieldName='User'
      fieldValue={user.email}
      onSubmit={() => {
        void deleteUser();
      }}
      onCancel={onCancel}
    >
      {(userResources.products ?? []).length > 0 && (
        <ProductUnassignment
          resources={{
            products: userResources.products as Product[],
          }}
        />
      )}
    </DeleteFormModal>
  );
};

export default DeleteUserFormModal;
